import {inject, Injectable} from "@angular/core";
import {collection, doc, Firestore, getDoc, getDocs} from "@angular/fire/firestore";
import {UserService} from "../../tab1/user.service";

export interface IPlan {
  name: PLANS;
  startDate?: number;
  endDate?: number;
}

interface IContract {
  businessCustomer: boolean;
  businessCustomerEndDate: number;
  businessCustomerStartDate: number;
  contractType: 'degustacao' | 'contract';
}

export enum PLANS {
  GOLD = 'gold',
  BRONZE = 'bronze',
  SILVER = 'silver',
  FREE = 'free'
}

@Injectable({providedIn: 'root'})
export class SettingsService {
  SETTINGS: Record<string, boolean> | undefined;
  PLAN: IPlan | undefined;
  IS_CONTRACT_VALID: boolean = false;

  /** dependency injections */
  private firestore = inject(Firestore);
  private userService = inject(UserService);

  /**
   * Bootstraps a aplicação com as configurações do usuário.
   */
  async getUserSettings() {
    const cardID = this.userService.getCardID();
    if (cardID) {
      const companyID = await this.getUserCompanyID(cardID);
      if (companyID) {
        this.SETTINGS = await this.getCompanySettings(companyID);
        this.IS_CONTRACT_VALID = await this.getCompanyStatus(companyID);
        this.PLAN = await this.getPlan(companyID, 'company');
      } else {
        this.PLAN = await this.getPlan(cardID, 'user');
      }
    return this.SETTINGS;
    }
    return undefined;
  }

  /**
   * Verifica se o usuário é parte de uma organização, caso positivo, retorna
   * o ID da mesma, caso negativo, retorna null.
   * @param {string} cardID
   */
  async getUserCompanyID(cardID: string): Promise<string | null> {
    const ref = collection(this.firestore, `cards/${cardID}/company/`);
    const req = await getDocs(ref);
    if (req.empty) return null;
    return req.docs[0].id;
  }

  /**
   * Retorna as configurações de uma empresa.
   * @param {string} companyID
   */
  async getCompanySettings(companyID: string): Promise<Record<string, boolean>> {
    const ref = doc(this.firestore, `settings/${companyID}/`);
    const data = await getDoc(ref);
    return data.data() as Record<string, boolean>;
  }

  /**
   * Retorna o plano do usuário corrente.
   * @param {string} docID representa o documento, pode ser card ou empresa
   * @param {'user' | 'company'} type define o tipo de documento
   */
  private async getPlan(docID: string, type: 'user' | 'company'): Promise<IPlan | undefined> {
      const ref = doc(
        this.firestore,
        `${type === 'user' ? 'cards' : 'companies'}/${docID}/settings/plan`);
      const planRef = await getDoc(ref);
      return planRef.exists() ?
        /** plano existe, retorna plano */
        planRef.data() as IPlan :
        /** plano não existe, checa se empresa tem contrato */
        (this.SETTINGS && this.IS_CONTRACT_VALID) ?
          {name: PLANS.SILVER} as IPlan :
          /** contrato é inválido ou não existe, prata */
          {name: PLANS.SILVER} as IPlan;
  }

  /**
   * Verifica se o contrato da empresa ainda é válido.
   * @todo abstrair para server side.
   * @param {string} companyID
   * @return {Promise<boolean>}
   */
  private async getCompanyStatus(companyID: string): Promise<boolean> {
    const ref = doc(this.firestore, `companies/${companyID}/internal/private/`);
    const dataRef = await getDoc(ref);
    if (dataRef.exists()) {
      const contractData = dataRef.data() as IContract;
      const {businessCustomerStartDate, businessCustomerEndDate} = contractData;
      const today = Date.now();
      return today >= businessCustomerStartDate && today <= businessCustomerEndDate;
    }
    return false;
  }
}
